import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Icon,
  Input,
  InputForm,
  SwitchForm,
} from '@jobcost-monorepo/ui';
import { apiClient } from '@jobcost-monorepo/utils';
import {
  BASE_URL,
  ICustomer,
  IListCustomerFetchParams,
  MN_CUSTOMERS,
} from '@jobcost-monorepo/crud';
import DropdownCrud from '@jobcost-monorepo/crud/ui/DropdownCrud';
import { useFieldArray, useFormContext, UseFormReturn } from 'react-hook-form';

interface Props {
  methods: UseFormReturn<
    {
      emails: {
        email: string;
      }[];
      IsEmailForOrdersToSend: boolean;
      emailOrders: string;
      IsEmailForInvoiceToSend: boolean;
      emailInvoice: string;
      IsEmailForTimesheetToSend: boolean;
      emailTimesheet: string;
      customerName: string;
    },
    any,
    undefined
  >;
}

const SendFormBody = ({ methods }: Props) => {
  const { t } = useTranslation(['timesheets']);
  const {
    getValues,
    register,
    setValue,
    formState: { errors },
    trigger,
    clearErrors,
  } = methods;
  const { watch } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    name: 'emails',
  });
  const id = watch('id');

  const getCustomer = async (id: number) => {
    const { data } = await apiClient.get<ICustomer>({
      url: `${BASE_URL}/${MN_CUSTOMERS.toLowerCase()}/${id}`,
    });
    setValue('emailInvoice', data?.emailInvoice ? data?.emailInvoice : '');
    setValue('customerName', data?.name ? data?.name : '');
    setValue('emailOrders', data?.emailOrder ? data?.emailOrder : '');
    setValue(
      'emailTimesheet',
      data?.emailTimesheet ? data?.emailTimesheet : '',
    );
    setValue('IsEmailForInvoiceToSend', false);
    setValue('IsEmailForOrdersToSend', false);
    setValue('IsEmailForTimesheetToSend', !!data?.emailTimesheet);
    clearErrors();
  };

  const handleAddEmail = async () => {
    clearErrors();
    const isValid = await trigger('emails');
    if (isValid) {
      append({ email: getValues('emails.0.email') });
      setValue('emails.0.email', '');
    }
  };

  useEffect(() => {
    if (id) {
      getCustomer(id);
    } else {
      setValue('emailInvoice', '');
      setValue('customerName', '');
      setValue('emailOrders', '');
      setValue('emailTimesheet', '');
      setValue('IsEmailForInvoiceToSend', false);
      setValue('IsEmailForOrdersToSend', false);
      setValue('IsEmailForTimesheetToSend', false);
    }
  }, [id]);

  return (
    <div className="flex flex-col gap-5 w-full max-h-[55vh] overflow-y-auto px-4 sm:max-h-[70vh]">
      <div className="flex flex-row gap-2 w-full">
        <div className="w-full">
          <DropdownCrud<ICustomer, IListCustomerFetchParams>
            moduleName={MN_CUSTOMERS}
            linkedField="id"
            linkedFieldForDefaultValue="name"
            label={t('email.form.customer')}
            width="w-full"
          />
        </div>
        <InputForm
          errorName={'customerName'}
          label={t('email.form.clientName')}
          name="customerName"
          disabled={false}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between w-full gap-5">
        <div className="flex flex-row w-full items-center gap-2">
          <InputForm
            errorName={'emailTimesheet'}
            label={t('email.form.emailTimesheet')}
            name="emailTimesheet"
            disabled={false}
          />
          <div className="flex flex-1 w-full">
            <SwitchForm
              name="IsEmailForTimesheetToSend"
              id="IsEmailForTimesheetToSend"
            />
          </div>
        </div>

        <div className="flex flex-row w-full items-center gap-2">
          <InputForm
            errorName={'emailInvoice'}
            label={t('email.form.emailInvoice')}
            name="emailInvoice"
            disabled={false}
          />
          <div className="flex flex-1 w-full">
            <SwitchForm
              name="IsEmailForInvoiceToSend"
              id="IsEmailForInvoiceToSend"
            />
          </div>
        </div>

        <div className="flex flex-row w-full items-center gap-2">
          <InputForm
            errorName={'emailOrders'}
            label={t('email.form.emailOrders')}
            name="emailOrders"
            disabled={false}
          />
          <div className="flex flex-1 w-full">
            <SwitchForm
              name="IsEmailForOrdersToSend"
              id="IsEmailForOrdersToSend"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1 w-full">
        <label className="text-sm font-medium text-gray-700 dark:text-white">
          {t('email.form.insertEmails')}
        </label>

        <div className="flex flex-row gap-2 items-center">
          <Input {...register('emails.0.email')} />
          <Button
            type="button"
            variant="subtle"
            onClick={() => handleAddEmail()}
          >
            {t('email.form.add')}
          </Button>
        </div>

        <div className="h-4 text-xs my-1 ml-1 text-red overflow-hidden">
          {errors.emails && errors.emails[0]?.email?.message && (
            <p>{t('email.error.' + errors.emails[0]?.email?.message)}</p>
          )}
          {errors.emails?.message && !errors.emails[0]?.email?.message && (
            <p>{t(errors?.emails?.message)}</p>
          )}
        </div>

        {fields.length > 1 && (
          <div className="flex flex-col gap-2">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-white">
                {t('email.list')}
              </p>
            </div>
            <div className="flex flex-col gap-2 text-grey dark:text-gray">
              {fields.map((field, index) => {
                if (index !== 0) {
                  return (
                    <div className="flex flex-row gap-2 w-fit" key={field.id}>
                      <Input readOnly {...register(`emails.${index}.email`)} />
                      <div
                        className="flex items-center cursor-pointer"
                        onClick={() => remove(index)}
                      >
                        <Icon
                          iconName="remove"
                          className="size-5 hover:opacity-50 fill-grey stroke-grey dark:fill-gray dark:stroke-gray"
                        />
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendFormBody;
